import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import MarketingHeader from "@/components/MarketingHeader";

function formatErr(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("If that email is registered, a reset link is on its way.");
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <MarketingHeader showSignIn={false} />
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <Link
            to="/login"
            data-testid="forgot-back-to-login"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-neutral-500 font-mono hover:text-white transition-colors"
          >
            <ArrowLeft className="w-3 h-3" strokeWidth={1.8} />
            Back to sign in
          </Link>
          <h2 className="font-display text-3xl font-semibold mt-4 tracking-tight">
            Reset your password
          </h2>
          <p className="mt-3 text-sm text-neutral-400 leading-relaxed">
            Enter the email on your OutreachOS account and we'll send you a
            link to set a new password.
          </p>

          {sent ? (
            <div
              data-testid="forgot-success-state"
              className="mt-6 rounded-md border border-emerald-500/20 bg-emerald-500/5 p-4 text-sm text-emerald-200 flex gap-3"
            >
              <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={1.8} />
              <div>
                <div className="font-medium">Check your inbox</div>
                <div className="mt-1 text-emerald-200/80">
                  If <span className="font-mono">{email}</span> matches an
                  account, a reset link is on its way. The link expires in 1
                  hour.
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-6 space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                data-testid="forgot-email-input"
                required
                className="w-full bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2.5 text-sm"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="forgot-submit-button"
                className="w-full flex items-center justify-center gap-2 bg-white text-black hover:bg-neutral-200 transition-colors py-3 rounded-md font-medium text-sm disabled:opacity-50"
              >
                {busy ? (
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
                ) : (
                  <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
                )}
                Send reset link
              </button>
            </form>
          )}

          <p className="mt-6 text-xs text-neutral-600 leading-relaxed">
            Remembered your password?{" "}
            <Link
              to="/login"
              data-testid="forgot-link-login"
              className="text-white hover:underline"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
